import { type Dayjs } from 'dayjs';
import { useCallback, useMemo } from 'react';
import { useConfigs } from 'shared/src/queries';
import { isNil } from 'shared/src/utils';

export const useGetNextArrivalDay = (
  date: Dayjs | null,
  deliveryType?: 'today' | 'shak' | 'standard' | 'reserved' | 'third_pl',
) => {
  const { data: configs } = useConfigs();

  const holidays = useMemo<{ [key: string]: boolean }>(() => {
    const list = [...(configs?.delivery_holidays ?? [])];
    if (deliveryType === 'shak') {
      list.push(...(configs?.shak_delivery.holidays ?? []));
    }
    return list.reduce<{ [key: string]: boolean }>((acc, cur) => {
      acc[cur] = true;
      return acc;
    }, {});
  }, [configs?.delivery_holidays, configs?.shak_delivery.holidays, deliveryType]);

  // 월~토 영업일 기준 (단, 토요일이 공휴일인 경우 제외)
  const isBusinessDay = useCallback(
    (day: Dayjs) => {
      const dayOfWeek = day.day();
      const dateKey = day.format('YYYY-MM-DD');
      // 일요일 제외
      if (dayOfWeek === 0) return false;
      // 공휴일 제외 (토요일이 공휴일인 경우도 포함)
      return !holidays[dateKey];
    },
    [holidays],
  );

  return useMemo(() => {
    if (isNil(date)) return null;

    // 입력 date가 이미 영업일이면 그대로 사용 (native iOS/Android의
    // calculateEstimatedDeliveryDate / calculateEstimatedArrival와 정합 —
    // start + N 영업일 누적의 결과가 영업일이면 그 날 도착으로 본다).
    // 휴일(일/공휴일)이면 다음 영업일까지만 진행.
    let current = date;
    while (!isBusinessDay(current)) {
      current = current.add(1, 'day');
    }

    return current;
  }, [date, isBusinessDay]);
};